import { Theme, useTheme } from '@emotion/react';
import dayjs from 'dayjs';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { BadgeProps } from '@/components/atoms/Badge/Badge';
import { useHistory, useParams } from 'react-router';
import RestaurantSelector from '@/components/molecules/RestaurantSelector';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, fetchDataProps, Table, TableRef } from '@/components/atoms/Table';
import { Routes } from '@/core/router/routes.types';
import { useSearchOrdersMutation } from '@/services/innovorder/orders/order.endpoints';
import { getBrandType } from '@/redux/brand';
import { getAssertedUserBrandId, getUserRestaurants, getUserRole, getUserToken } from '@/redux/user';
import { UserRestaurant, UserRoles } from '@/services/innovorder/user/user.type';
import { BrandType } from '@/services/innovorder/brand/brand.type';
import { useDownloadCustomerTicketMutation } from '@/services/innovorder/customer/customer.endpoints';
import { useTicketExport } from '@/hooks/useTicketExport';
import { OmnichannelOrder } from '@/services/innovorder/orders/order.type';
import { getParamFromQueryString, replaceParamInUrl } from '@/utils/history';
import { TableRow } from '@/components/atoms/Table/Table.type';
import { Button } from '@/components/atoms/Button';
import { SVGIcon, SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import { DropDown } from '@/components/atoms/DropDown';
import { computeText } from '@/locales/utils';
import ExportModal from './ExportModal';
import { Container } from './OrderList.style';
import {
    convertTableFiltersToQueryFilters,
    convertTableSortToQuerySort,
    getDefaultHiddenColumns,
    getOrdersTableColumnsVM,
    getOrdersTableRowsVM,
    getRestaurantsCondition,
} from './utils';
import { useGetExportButtons } from './hooks/useGetExportButtons';
import PageHeader from './OrderListHeader';
import DateRangeSelector, { DateRange } from './DateRangeSelector';
import EmptyOrdersIcon from './empty-orders.svg?react';

const defaultSortBy = [{ id: 'orderDate', desc: true }];

const DEFAULT_DATE_PRESET_DAYS = 7;

const buildDefaultDateRange = (days: number): DateRange => {
    return {
        startDate: dayjs().subtract(days, 'day').startOf('day').toISOString(),
        endDate: dayjs().endOf('day').toISOString(),
    };
};

type OrderListData = {
    omnichannelOrders?: OmnichannelOrder[];
    orders?: OmnichannelOrder[];
    count?: number;
    nextCursor?: string | null;
    hasMore?: boolean;
};

export interface OrderListProps {
    token: string | undefined;
    brandId?: number;
    restaurants?: UserRestaurant[] | undefined;
    userRole?: UserRoles;
    customerId?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fetchOrders: (params: any) => void;
    data?: OrderListData;
    isLoading: boolean;
    brandType?: BrandType;
    theme: Theme;
    headerProps: { title: string; subTitle: string; showRestaurantSelector: boolean };
}

export const OrderList: React.FunctionComponent<OrderListProps> = ({
    token,
    brandId,
    restaurants,
    userRole,
    customerId,
    fetchOrders,
    data,
    isLoading,
    brandType,
    theme,
    headerProps,
}) => {
    const intl = useIntl();
    const history = useHistory();
    const { exportButtons, isExportModalVisible, setExportModalVisibility } = useGetExportButtons();
    const { startExport, isExporting } = useTicketExport(Number(customerId) || 0);
    const [downloadCustomerTicket] = useDownloadCustomerTicketMutation();

    const canExportOrders = userRole ? ![UserRoles.admin, UserRoles.brandGroup].includes(userRole) : false;

    const [selectedRestaurantId, setSelectedRestaurantId] = useState(
        Number(getParamFromQueryString(history.location.search, 'restaurant')) || 0,
    );

    const restoredCursorState = useMemo(() => {
        const state = history.location.state as {
            cursorHistory?: (string | null)[];
            fingerprint?: string;
            dateRange?: DateRange;
            datePreset?: number;
        } | null;
        return state?.cursorHistory ? state : null;
    }, []);

    const [datePreset, setDatePreset] = useState(restoredCursorState?.datePreset ?? DEFAULT_DATE_PRESET_DAYS);
    const [dateRange, setDateRange] = useState<DateRange>(
        () => restoredCursorState?.dateRange ?? buildDefaultDateRange(DEFAULT_DATE_PRESET_DAYS),
    );

    const handleDateRangeChange = useCallback((newDateRange: DateRange, preset: number) => {
        setDateRange(newDateRange);
        setDatePreset(preset);
    }, []);

    // Cursor pagination state
    const tableRef = useRef<TableRef>(null);
    const cursorHistoryRef = useRef<(string | null)[]>(restoredCursorState?.cursorHistory ?? [null]);
    const [lastResponse, setLastResponse] = useState<{ nextCursor: string | null; hasMore: boolean }>({
        nextCursor: null,
        hasMore: false,
    });
    const currentPageIndexRef = useRef(0);
    const currentPageSizeRef = useRef(DEFAULT_PAGE_SIZE);
    const lastParamsFingerprintRef = useRef<string>(restoredCursorState?.fingerprint ?? '');

    React.useEffect(() => {
        if (data) {
            const orders = data.orders ?? data.omnichannelOrders ?? [];

            // If we got 0 results on page > 0, fall back to page 0
            if (orders.length === 0 && currentPageIndexRef.current > 0) {
                cursorHistoryRef.current = [null];
                currentPageIndexRef.current = 0;
                setLastResponse({ nextCursor: null, hasMore: false });
                tableRef.current?.gotoPage?.(0);
                return;
            }

            setLastResponse({
                nextCursor: data.nextCursor ?? null,
                hasMore: data.hasMore ?? false,
            });
            if (data.nextCursor) {
                const next = [...cursorHistoryRef.current];
                next[currentPageIndexRef.current + 1] = data.nextCursor;
                cursorHistoryRef.current = next;
            }
            history.replace({
                ...history.location,
                state: {
                    cursorHistory: cursorHistoryRef.current,
                    fingerprint: lastParamsFingerprintRef.current,
                    dateRange,
                    datePreset,
                },
            });
        }
    }, [data]);

    const handleFetchDataCursor = useCallback(
        ({ pageIndex, pageSize, globalFilter, sortBy, columnFilters }: fetchDataProps) => {
            if (token) {
                const sortOrder = convertTableSortToQuerySort(sortBy);
                const filters = convertTableFiltersToQueryFilters(columnFilters);
                const { startDate: _, endDate: __, ...restFilters } = filters;

                const paramsFingerprint = JSON.stringify({
                    sortOrder,
                    filters: restFilters,
                    globalFilter,
                });
                const isNewSearch = paramsFingerprint !== lastParamsFingerprintRef.current;
                if (isNewSearch) {
                    cursorHistoryRef.current = [null];
                    setLastResponse({ nextCursor: null, hasMore: false });
                    lastParamsFingerprintRef.current = paramsFingerprint;
                    if (pageIndex && pageIndex > 0) {
                        tableRef.current?.gotoPage?.(0);
                        return;
                    }
                }

                const currentPageIndex = isNewSearch ? 0 : pageIndex ?? DEFAULT_PAGE_INDEX;
                currentPageIndexRef.current = currentPageIndex;
                currentPageSizeRef.current = pageSize ?? DEFAULT_PAGE_SIZE;

                const cursor =
                    currentPageIndex > 0 ? cursorHistoryRef.current[currentPageIndex] ?? undefined : undefined;

                fetchOrders({
                    brandId,
                    restaurantIds: getRestaurantsCondition(selectedRestaurantId, userRole, restaurants),
                    token,
                    search: globalFilter,
                    filters: restFilters,
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    limit: pageSize ?? DEFAULT_PAGE_SIZE,
                    cursor,
                    order: sortOrder,
                    customerId,
                });
            }
        },
        [token, fetchOrders, brandId, selectedRestaurantId, userRole, restaurants, customerId, dateRange],
    );

    const handleDownloadTicket = useCallback(
        (orderId: string) => {
            downloadCustomerTicket({ orderId, token });
        },
        [downloadCustomerTicket, token],
    );

    const ordersList = data?.orders ?? data?.omnichannelOrders;

    const columns = useMemo(() => getOrdersTableColumnsVM(intl, brandType, !!customerId, true), [intl, brandType]);
    const rows = useMemo(
        () => getOrdersTableRowsVM(ordersList, intl, theme, handleDownloadTicket),
        [ordersList, intl, theme, customerId, handleDownloadTicket],
    );

    const fetchDataTotalCount = useMemo(() => {
        if (!ordersList?.length) return 0;
        const pageIndex = currentPageIndexRef.current;
        return (pageIndex + 1 + (lastResponse.hasMore ? 1 : 0)) * currentPageSizeRef.current;
    }, [ordersList, lastResponse.hasMore]);

    const customRowProps = (row: TableRow) => {
        const status = row.status?.value;

        return {
            style: {
                backgroundColor:
                    (status as BadgeProps)?.text === 'order.status.INCOHERENT'
                        ? theme.color.palePink100
                        : theme.color.white100,
            },
        };
    };

    const handleRestaurantChange = useCallback(
        (restaurantId: number) => {
            setSelectedRestaurantId(restaurantId);
            replaceParamInUrl(history, 'restaurant', String(restaurantId));
        },
        [history],
    );

    const toggleExport = useCallback(() => {
        setExportModalVisibility((open: boolean) => !open);
    }, [setExportModalVisibility]);

    const defaultHiddenColumns = useMemo(() => {
        if (!brandType) return [];
        return getDefaultHiddenColumns(brandType);
    }, [brandType]);

    const handleDownloadAll = useCallback(() => {
        startExport();
    }, [startExport]);

    const handleDownloadSelectedRange = useCallback(() => {
        startExport({ startDate: dateRange.startDate, endDate: dateRange.endDate });
    }, [startExport, dateRange]);

    const downloadTicketsItems = useMemo(
        () => [
            {
                value: 'all',
                label: computeText(intl, 'customer.ordersList.downloadChoice.all'),
                onClick: handleDownloadAll,
            },
            {
                value: 'selectedRange',
                label: computeText(intl, 'customer.ordersList.downloadChoice.selectedRange'),
                onClick: handleDownloadSelectedRange,
            },
        ],
        [intl, handleDownloadAll, handleDownloadSelectedRange],
    );

    return (
        <Container>
            <PageHeader title={headerProps.title} subtitle={headerProps.subTitle}>
                {customerId && (
                    <DropDown
                        type="text"
                        items={downloadTicketsItems}
                        isUnstyledButton
                        button={
                            <Button disabled={isExporting} isLoading={isExporting} buttonType="primary">
                                <SVGIcon icon={SVGIcons.EXPORTS} size={16} color={theme.color.white100} />
                                <FormattedMessage id="customer.ordersList.downloadTickets" />
                            </Button>
                        }
                    />
                )}
                <DateRangeSelector selectedPreset={datePreset} dateRange={dateRange} onChange={handleDateRangeChange} />
                {headerProps.showRestaurantSelector && (
                    <RestaurantSelector
                        buttonLabel={
                            selectedRestaurantId
                                ? restaurants?.find((restaurant) => restaurant.restaurantId === selectedRestaurantId)
                                      ?.name || '???'
                                : intl.formatMessage({ id: 'orderList.filters.allRestaurants' })
                        }
                        restaurants={restaurants || []}
                        selected={selectedRestaurantId}
                        onChange={handleRestaurantChange}
                        headerLabel="orderList.filters.restaurantHeader"
                    />
                )}
            </PageHeader>
            {brandType && (
                <Table
                    rows={rows}
                    customRowProps={customRowProps}
                    emptyMessageId={
                        datePreset > 0
                            ? 'table.noRowsAfterFilter.dateRangePresetHint'
                            : 'table.noRowsAfterFilter.dateRangeCustomHint'
                    }
                    emptyMessageValues={datePreset > 0 ? { days: datePreset } : undefined}
                    emptyIcon={<EmptyOrdersIcon width={120} height={120} />}
                    emptyTableChildren={<FormattedMessage id="table.noRowsAfterFilter.dateRangeAction" />}
                    columns={columns}
                    hasPagination
                    hasUrlNavigation
                    isFilterable
                    isSortable
                    defaultSortBy={defaultSortBy}
                    fetchData={handleFetchDataCursor}
                    fetchDataTotalCount={fetchDataTotalCount}
                    tableRef={tableRef}
                    isLoading={isLoading}
                    onRowClickPath={Routes.OrderDetails}
                    toolbarButtons={canExportOrders ? exportButtons : []}
                    columnSelectorId={customerId ? 'customerOrderList' : 'orderList'}
                    defaultHiddenColumns={defaultHiddenColumns}
                    hasFirstAndLastPage={false}
                    hasFirstPage
                    hideTotalPages
                    hidePageNumber
                />
            )}
            {isExportModalVisible && (
                <ExportModal
                    width={680}
                    onHide={toggleExport}
                    brandId={brandId}
                    restaurantId={selectedRestaurantId}
                    customerId={customerId}
                />
            )}
        </Container>
    );
};

const withOrderListParams = (WrappedComponent: React.ComponentType<OrderListProps>) => {
    return () => {
        const theme = useTheme();
        const token = useSelector(getUserToken);
        const brandId = useSelector(getAssertedUserBrandId);
        const customerId = Number(useParams<{ customerId?: string }>()?.customerId) || undefined;
        const brandType = useSelector(getBrandType);
        const restaurants = useSelector(getUserRestaurants);
        const userRole = useSelector(getUserRole);
        const [fetchOrders, result] = useSearchOrdersMutation();

        return (
            <WrappedComponent
                token={token}
                brandId={brandId}
                restaurants={restaurants}
                userRole={userRole}
                customerId={Number(customerId) || undefined}
                fetchOrders={fetchOrders}
                data={result.data}
                isLoading={result.isLoading}
                brandType={brandType}
                theme={theme}
                headerProps={{
                    title: 'orderList.title',
                    subTitle: 'orderList.subtitle',
                    showRestaurantSelector: true,
                }}
            />
        );
    };
};
export default withOrderListParams(OrderList);
